import {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from 'react';
import { useGetTests, type Test } from '../hooks/tests/useTests';
import { useGetProfile } from '../hooks/profile/useProfile';
import { BRAND_ID } from '../config/brand';

interface TestContextValue {
  tests: Test[];
  selectedTestId: string | null;
  setSelectedTestId: (id: string) => void;
  isFetching: boolean;
  needsProfiling: boolean;
  needsOnboarding: boolean;
}

const TestContext = createContext<TestContextValue | null>(null);

export const SELECTED_TEST_ID_KEY = 'selectedTestId';

export function TestProvider({ children }: { children: ReactNode }) {
  const { tests, isFetching: isTestsFetching } = useGetTests();
  const {
    profile,
    isFetching: isProfileFetching,
    isSuccess: isProfileSuccess,
  } = useGetProfile();

  const [selectedTestId, setSelectedTestIdState] = useState<string | null>(() =>
    localStorage.getItem(SELECTED_TEST_ID_KEY),
  );
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  const setSelectedTestId = (id: string) => {
    localStorage.setItem(SELECTED_TEST_ID_KEY, id);
    setSelectedTestIdState(id);
    setNeedsOnboarding(false);
    window.dispatchEvent(new CustomEvent('testIdChanged', { detail: id }));
  };

  useEffect(() => {
    if (
      isTestsFetching ||
      isProfileFetching ||
      !isProfileSuccess ||
      tests.length === 0
    )
      return;

    const storedId = localStorage.getItem(SELECTED_TEST_ID_KEY);

    // 1. localStorage ha un id valido → usalo
    if (storedId && tests.some((t) => t._id === storedId)) {
      setSelectedTestIdState(storedId);
      return;
    }

    // 2. Il brand corrente ha un favoriteTest valido → usalo
    const currentBrand = profile?.brands?.find((b) => b.id === BRAND_ID);
    const favoriteTest = currentBrand?.favoriteTest;
    if (favoriteTest && tests.some((t) => t._id === favoriteTest)) {
      setSelectedTestId(favoriteTest);
      return;
    }

    // 3. Nessuna info → mostra onboarding
    setSelectedTestIdState(null);
    setNeedsOnboarding(true);
  }, [tests, profile, isTestsFetching, isProfileFetching, isProfileSuccess]);

  const isFetching = isTestsFetching || isProfileFetching;
  const currentBrand = profile?.brands?.find((b) => b.id === BRAND_ID);
  const needsProfiling = isProfileSuccess && currentBrand?.hasProfilation !== true;

  return (
    <TestContext.Provider
      value={{
        tests,
        selectedTestId,
        setSelectedTestId,
        isFetching,
        needsProfiling,
        needsOnboarding,
      }}
    >
      {children}
    </TestContext.Provider>
  );
}

export function useTestContext() {
  const ctx = useContext(TestContext);
  if (!ctx) throw new Error('useTestContext must be used within TestProvider');
  return ctx;
}
